import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { FaLock, FaTimes } from 'react-icons/fa'
import CreatableSelect from 'react-select/creatable'
import useFetch from '../../../../_fetch'
import { currencies } from '../../../../_utils'
import { IPartnership, PartnershipType } from '../types'

type PartnershipFormState = {
    name: string
    type: PartnershipType
    value: string
    currency: string
    startDate: string
    expiryDate: string
    cookieDays: string
}

function getTodayDateString() {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function createInitialForm(): PartnershipFormState {
    return {
        name: '',
        type: 'sale',
        value: '',
        currency: 'DKK',
        startDate: getTodayDateString(),
        expiryDate: '',
        cookieDays: '30',
    }
}

function stripCommonSubdomain(hostname: string) {
    return hostname.replace(/^www\d*\./i, '')
}

function cleanDomain(input: string) {
    try {
        const url = input.startsWith('http') ? input : `https://${input}`
        const parsed = new URL(url)
        return stripCommonSubdomain(parsed.hostname.toLowerCase())
    } catch {
        return null
    }
}

function isValidDomain(domain: string) {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
}

function getValueLabel(type: PartnershipType) {
    if (type === 'sale') return 'Sale percentage'
    if (type === 'click') return 'Value per click'
    return 'Value'
}

function getTypeBadgeVariant(type: PartnershipType) {
    if (type === 'sale') return 'success'
    if (type === 'click') return 'primary'
    return 'warning'
}

function formatPayout(partnership: IPartnership) {
    if (partnership.type === 'sale') return `${partnership.value}%`
    if (partnership.type === 'click') return `${partnership.value} ${partnership.currency} per click`
    return `${partnership.value} ${partnership.currency} per lead`
}

function getApiErrorMessage(err: any, fallback: string) {
    if (typeof err !== 'string') return fallback

    try {
        const parsed = JSON.parse(err)
        if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error
        if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message
    } catch {
        // Use plain text below.
    }

    return err.trim() || fallback
}

const LinkScriptPartnerships: React.FC = () => {
    const navigate = useNavigate()
    const { doFetch } = useFetch()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [modalError, setModalError] = useState('')
    const [success, setSuccess] = useState('')
    const [partnerships, setPartnerships] = useState<IPartnership[]>([])
    const [form, setForm] = useState<PartnershipFormState>(createInitialForm)
    const [formDomains, setFormDomains] = useState<string[]>([])
    const [showModal, setShowModal] = useState(false)
    const [domainError, setDomainError] = useState('')

    const valueLabel = useMemo(() => getValueLabel(form.type), [form.type])

    const loadPartnerships = useCallback(async () => {
        try {
            setLoading(true)
            setError('')
            const res = await doFetch('/byelink/partnerships')
            if (res.status >= 400) throw await res.text()
            const data = await res.json()
            setPartnerships(Array.isArray(data) ? data : [])
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Could not load partnerships'))
        } finally {
            setLoading(false)
        }
    }, [doFetch])

    useEffect(() => {
        loadPartnerships()
    }, [loadPartnerships])

    const updateForm = (key: keyof PartnershipFormState, value: string) => {
        if (key === 'type') {
            setForm((prev) => {
                const nextType = value as PartnershipType
                if (nextType === 'click') {
                    return { ...prev, type: nextType, cookieDays: '0' }
                }
                if (nextType === 'sale') {
                    return {
                        ...prev,
                        type: nextType,
                        currency: 'DKK',
                        cookieDays: prev.cookieDays === '0' ? '30' : prev.cookieDays,
                    }
                }
                return {
                    ...prev,
                    type: nextType,
                    cookieDays: prev.cookieDays === '0' ? '30' : prev.cookieDays,
                }
            })
            return
        }

        setForm((prev) => ({ ...prev, [key]: value }))
    }

    const resetForm = () => {
        setForm(createInitialForm())
        setFormDomains([])
        setDomainError('')
    }

    const openCreateModal = () => {
        setShowModal(true)
        setError('')
        setModalError('')
        setSuccess('')
        resetForm()
    }

    const closeModal = () => {
        setShowModal(false)
        setModalError('')
        resetForm()
    }

    const addDomain = (option: any) => {
        const raw = option?.value?.trim()?.toLowerCase()
        if (!raw) return

        const domain = cleanDomain(raw)
        if (!domain || !isValidDomain(domain)) {
            setDomainError('Invalid domain')
            return
        }

        setDomainError('')
        setFormDomains((current) => (current.includes(domain) ? current : [...current, domain]))
    }

    const removeDomain = (domain: string) => {
        setFormDomains((current) => current.filter((entry) => entry !== domain))
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setModalError('')
        setSuccess('')

        if (!form.name.trim()) {
            setModalError('Partner name is required')
            return
        }

        if (!form.startDate) {
            setModalError('Start date is required')
            return
        }

        const valueNumber = Number(form.value)
        if (!Number.isFinite(valueNumber) || valueNumber < 0) {
            setModalError('Value must be a positive number')
            return
        }

        const cookieDays = form.type === 'click' ? 0 : Number(form.cookieDays)
        if (form.type !== 'click' && (!Number.isInteger(cookieDays) || cookieDays <= 0)) {
            setModalError('Cookie duration must be at least 1 day')
            return
        }
        if (form.type !== 'click' && cookieDays > 60) {
            setModalError('Cookie duration cannot exceed 60 days')
            return
        }

        try {
            setSaving(true)

            const payload = {
                name: form.name,
                domains: formDomains,
                type: form.type,
                value: valueNumber,
                currency: form.type === 'sale' ? 'DKK' : form.currency,
                startDate: form.startDate,
                expiryDate: form.expiryDate || null,
                cookieDays,
            }

            const res = await doFetch('/byelink/partnerships', payload)
            if (res.status >= 400) throw await res.text()

            const saved = await res.json()
            setPartnerships((prev) => [saved, ...prev])
            setSuccess('Partnership created')
            closeModal()
        } catch (err: any) {
            setModalError(getApiErrorMessage(err, 'Could not save partnership'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="pe-2">
            {!showModal && error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-3">
                <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                    <div>
                        <h5 className="mb-1">Partnerships</h5>
                        <div className="text-muted small">
                            Create and manage partner agreements for sale, click, and lead payouts.
                        </div>
                    </div>
                    <Button onClick={openCreateModal}>Add partner</Button>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Partnership list</strong>
                    <span className="text-muted small">{partnerships.length} item{partnerships.length === 1 ? '' : 's'}</span>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="d-flex justify-content-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : !partnerships.length ? (
                        <div className="text-center py-4">
                            <div className="fw-semibold mb-1">No partnerships yet</div>
                            <div className="text-muted mb-3">Create your first partnership to get started.</div>
                            <Button onClick={openCreateModal}>Create partnership</Button>
                        </div>
                    ) : (
                        <Table responsive hover className="align-middle mb-0">
                            <thead style={{ backgroundColor: '#5b9bd5', color: '#ffffff' }}>
                                <tr>
                                    <th style={{ color: '#ffffff' }}>Partner</th>
                                    <th style={{ color: '#ffffff' }}>Type</th>
                                    <th style={{ color: '#ffffff' }}>Payout</th>
                                    <th style={{ color: '#ffffff' }}>Cookie</th>
                                    <th style={{ color: '#ffffff' }}>Start</th>
                                    <th style={{ color: '#ffffff' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {partnerships.map((partnership) => (
                                    <tr key={partnership.id}>
                                        <td>
                                            <div className="fw-semibold d-flex align-items-center gap-1">
                                                {partnership.name}
                                                {partnership.locked && (
                                                    <FaLock title="Locked by advertiser" style={{ color: '#6c757d', fontSize: '0.75rem' }} />
                                                )}
                                            </div>
                                            <div className="text-muted small">
                                                {Array.isArray(partnership.domains) && partnership.domains.length
                                                    ? partnership.domains.join(', ')
                                                    : 'No domains set'}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge bg={getTypeBadgeVariant(partnership.type)}>{partnership.type}</Badge>
                                        </td>
                                        <td>{formatPayout(partnership)}</td>
                                        <td>{partnership.type === 'click' ? 'N/A' : `${partnership.cookieDays} days`}</td>
                                        <td>{partnership.startDate}</td>
                                        <td className="text-end">
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button
                                                    variant="outline-dark"
                                                    size="sm"
                                                    onClick={() => navigate(`/link-script/partnerships/${partnership.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={closeModal} size="lg">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Add partnership</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalError && <Alert variant="danger">{modalError}</Alert>}
                        {domainError && <Alert variant="danger">{domainError}</Alert>}

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Partner name</Form.Label>
                                    <Form.Control
                                        value={form.name}
                                        onChange={(event) => updateForm('name', event.target.value)}
                                        placeholder="Example Partner"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Partner domains</Form.Label>
                                    <CreatableSelect
                                        placeholder="Add domain (e.g. partner.com)"
                                        isClearable
                                        formatCreateLabel={(value) => `Add "${value}"`}
                                        onChange={addDomain}
                                        onCreateOption={(value) => addDomain({ value })}
                                    />
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {formDomains.map((domain) => (
                                            <Badge key={domain} bg="info" className="d-flex align-items-center px-2 py-1">
                                                {domain}
                                                <FaTimes
                                                    className="ms-2"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => removeDomain(domain)}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Type</Form.Label>
                                    <Form.Select
                                        value={form.type}
                                        onChange={(event) => updateForm('type', event.target.value as PartnershipType)}
                                    >
                                        <option value="sale">Sale</option>
                                        <option value="click">Click</option>
                                        <option value="lead" disabled>Lead</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            {form.type !== 'sale' ? (
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Currency</Form.Label>
                                        <Form.Select
                                            value={form.currency}
                                            onChange={(event) => updateForm('currency', event.target.value)}
                                        >
                                            {currencies.map((currency) => (
                                                <option key={currency} value={currency}>
                                                    {currency}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            ) : null}
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{valueLabel}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={form.value}
                                        onChange={(event) => updateForm('value', event.target.value)}
                                        placeholder="0"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={form.startDate}
                                        onChange={(event) => updateForm('startDate', event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End date (optional)</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={form.expiryDate}
                                        onChange={(event) => updateForm('expiryDate', event.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            {form.type !== 'click' && (
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Cookie duration (days)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            max={60}
                                            value={form.cookieDays}
                                            onChange={(event) => updateForm('cookieDays', event.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        {form.type === 'click' && (
                            <Alert variant="info" className="mb-0">
                                Click type works with link automation only.
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeModal}>Close</Button>
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Saving...' : 'Create partnership'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

        </div>
    )
}

export default LinkScriptPartnerships
